import { useEffect, useState, useMemo, useCallback } from "react";
import debounce from "lodash/debounce";

export const useFilteredProducts = (
  products: any[],
  selectedDay: string,
  delay = 300
) => {
  const [filteredProducts, setFilteredProducts] = useState<any[]>([]);

  const shouldShowProduct = (
    product: any,
    selectedDay: string
  ): { show: boolean; badge?: string } => {
    const frequency = product?.frequency?.toLowerCase();

    if (!frequency) return { show: true };

    switch (frequency) {
      case "every day":
        return { show: true };

      case "can skip":
        return { show: true, badge: "Can Skip" };

      case "every night":
        return { show: true, badge: "Every Night" };

      case "when needed":
        return { show: true, badge: "When Needed" };

      case "mon, wed, fri":
        return {
          show: ["Monday", "Wednesday", "Friday"].includes(selectedDay),
          badge:
            selectedDay === "Monday" ||
            selectedDay === "Wednesday" ||
            selectedDay === "Friday"
              ? "MWF"
              : undefined,
        };

      case "tue, thu, sat":
        return {
          show: ["Tuesday", "Thursday", "Saturday"].includes(selectedDay),
          badge:
            selectedDay === "Tuesday" ||
            selectedDay === "Thursday" ||
            selectedDay === "Saturday"
              ? "TTS"
              : undefined,
        };

      case "sat or sun":
        return {
          show: ["Saturday", "Sunday"].includes(selectedDay),
          badge:
            selectedDay === "Saturday" || selectedDay === "Sunday"
              ? "Weekend"
              : undefined,
        };

      default:
        return { show: true };
    }
  };

  const getFilteredProducts = (products: any[], selectedDay: string) => {
    if (!products) return [];

    return products
      .filter((product) => !product.dont_use)
      .map((product) => ({
        ...product,
        frequencyInfo: shouldShowProduct(product, selectedDay),
      }))
      .filter((product) => product.frequencyInfo.show);
  };

  const debouncedFilter = useMemo(
    () =>
      debounce((products, selectedDay) => {
        const result = getFilteredProducts(products, selectedDay);
        setFilteredProducts(result);
      }, delay),
    [delay]
  );

  useEffect(() => {
    debouncedFilter(products, selectedDay);

    return () => {
      debouncedFilter.cancel();
    };
  }, [products, selectedDay, debouncedFilter]);

  return filteredProducts;
};
